// Design ELEMENTS — shapes/lines/arrows drawn on the project canvas. The SAME fractional
// coordinate model drives the live preview (VideoPlayer canvas) and the server export
// (backend elements_render.render_element_png), so what you see is what you get.
//
// Element object model (all positional values are fractions of the PROJECT canvas):
//   { id, kind:"element", element, x, y, w, h, fill, stroke, strokeWidth, opacity,
//     rotation, cornerRadius, start, end }
//   x,y  = center (0..1) · w,h = size (fraction of canvas W/H) · strokeWidth = fraction of W
//   rotation = degrees · cornerRadius = fraction of min(box) for rounded rects.

export const ELEMENT_TYPES = [
  { v: "rect", l: "Rectangle" },
  { v: "rounded", l: "Rounded Rectangle" },
  { v: "circle", l: "Circle" },
  { v: "ellipse", l: "Ellipse / Oval" },
  { v: "line", l: "Line" },
  { v: "arrow", l: "Arrow" },
  { v: "triangle", l: "Triangle" },
];

let _n = 0;
const uid = () => `el-${Date.now().toString(36)}-${(_n++).toString(36)}`;

export function newElement(type = "rect", atTime = 0, dur = 4) {
  const base = {
    id: uid(), kind: "element", element: type,
    x: 0.5, y: 0.5, opacity: 1, rotation: 0,
    start: Math.max(0, +atTime || 0), end: Math.max(1, (+atTime || 0) + dur),
  };
  if (type === "line" || type === "arrow") {
    return { ...base, w: 0.4, h: 0.02, fill: null, stroke: "#00F0FF", strokeWidth: 0.006 };
  }
  if (type === "circle") return { ...base, w: 0.22, h: 0.22, fill: "#8B5CF6", stroke: null, strokeWidth: 0 };
  if (type === "ellipse") return { ...base, w: 0.3, h: 0.18, fill: "#8B5CF6", stroke: null, strokeWidth: 0 };
  if (type === "triangle") return { ...base, w: 0.26, h: 0.24, fill: "#FFC53D", stroke: null, strokeWidth: 0 };
  if (type === "rounded") return { ...base, w: 0.34, h: 0.16, fill: "#00F0FF", stroke: null, strokeWidth: 0, cornerRadius: 0.22 };
  return { ...base, w: 0.34, h: 0.16, fill: "#00F0FF", stroke: null, strokeWidth: 0, cornerRadius: 0 }; // rect
}

// Draw an element on a 2D canvas context sized cw×ch. Mirrors backend render_element_png.
export function drawElement(ctx, o, cw, ch) {
  const cx = (o.x != null ? o.x : 0.5) * cw;
  const cy = (o.y != null ? o.y : 0.5) * ch;
  const w = Math.max(1, (o.w != null ? o.w : 0.3) * cw);
  const h = Math.max(1, (o.h != null ? o.h : 0.15) * ch);
  const sw = Math.max(0, (o.strokeWidth || 0) * cw);
  const fill = o.fill || null;
  const stroke = o.stroke || null;
  const type = o.element || "rect";
  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, o.opacity != null ? o.opacity : 1));
  ctx.translate(cx, cy);
  if (o.rotation) ctx.rotate((o.rotation * Math.PI) / 180);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  if (type === "line" || type === "arrow") {
    const half = w / 2;
    ctx.strokeStyle = stroke || "#FFFFFF";
    ctx.lineWidth = Math.max(2, sw || w * 0.02);
    ctx.beginPath();
    ctx.moveTo(-half, 0);
    ctx.lineTo(half, 0);
    ctx.stroke();
    if (type === "arrow") {
      const a = Math.max(ctx.lineWidth * 3, w * 0.06);
      ctx.beginPath();
      ctx.moveTo(half, 0);
      ctx.lineTo(half - a, -a * 0.75);
      ctx.moveTo(half, 0);
      ctx.lineTo(half - a, a * 0.75);
      ctx.stroke();
    }
    ctx.restore();
    return;
  }

  ctx.beginPath();
  if (type === "circle" || type === "ellipse") {
    ctx.ellipse(0, 0, w / 2, h / 2, 0, 0, Math.PI * 2);
  } else if (type === "triangle") {
    ctx.moveTo(0, -h / 2);
    ctx.lineTo(w / 2, h / 2);
    ctx.lineTo(-w / 2, h / 2);
    ctx.closePath();
  } else {
    const rad = type === "rounded" ? Math.min(w, h) * Math.min(0.5, o.cornerRadius || 0.22) : 0;
    if (rad > 0 && ctx.roundRect) ctx.roundRect(-w / 2, -h / 2, w, h, rad);
    else ctx.rect(-w / 2, -h / 2, w, h);
  }
  if (fill) { ctx.fillStyle = fill; ctx.fill(); }
  if (stroke && sw > 0) { ctx.strokeStyle = stroke; ctx.lineWidth = sw; ctx.stroke(); }
  ctx.restore();
}
